import { useEffect, useState, useRef } from "react";
import { MapPin, Clock } from "lucide-react";
import { API } from "@/lib/api";
import { formatDateIt } from "@/lib/timeSlots";

export default function TVDisplay() {
  const [data, setData] = useState(null);
  const [clock, setClock] = useState(new Date());
  const timer = useRef(null);
  const scrollRef = useRef(null);

  const fetchData = async () => {
    try {
      const res = await fetch(`${API}/tv/substitutions`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      /* keep last data on transient error */
    }
  };

  useEffect(() => {
    fetchData();
    const poll = setInterval(fetchData, 20000);
    timer.current = setInterval(() => setClock(new Date()), 1000);
    return () => { clearInterval(poll); clearInterval(timer.current); };
  }, []);

  const timeStr = clock.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const dateStr = data ? formatDateIt(data.date) : "";
  const todaySubs = data?.substitutions || [];
  const nextDay = data?.next_day || null;
  const showNext = todaySubs.length === 0 && nextDay && (nextDay.substitutions?.length || 0) > 0;
  const subs = showNext ? nextDay.substitutions : todaySubs;

  const niceDate = (d) => {
    if (!d) return "";
    const [y, m, dd] = d.split("-");
    const dt = new Date(Number(y), Number(m) - 1, Number(dd));
    return dt.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let raf;
    let dir = 1;
    let pause = 90;
    const step = () => {
      if (el.scrollHeight > el.clientHeight + 4) {
        if (pause > 0) {
          pause -= 1;
        } else {
          el.scrollTop += 0.6 * dir;
          if (el.scrollTop + el.clientHeight >= el.scrollHeight - 1) { dir = -1; pause = 140; }
          else if (el.scrollTop <= 0) { dir = 1; pause = 140; }
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [subs.length]);

  return (
    <div className="dark min-h-screen bg-background text-foreground flex flex-col p-8 md:p-14 overflow-hidden">
      <header className="flex items-start justify-between gap-6 border-b border-border pb-6">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-white p-1.5 shadow-sm">
            <img src="/logo.png" alt="Logo I.T.E. Alfonso Gallo" className="h-full w-full object-contain" data-testid="tv-logo" />
          </div>
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight leading-none">
              I.T.E. "Alfonso Gallo"
            </h1>
            <p className="text-muted-foreground text-lg md:text-xl mt-1">Sostituzioni del giorno — Aversa</p>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-5xl md:text-6xl font-bold tabular-nums tracking-tight" data-testid="tv-clock">{timeStr}</div>
          <div className="text-muted-foreground text-lg md:text-xl mt-1">{dateStr}</div>
        </div>
      </header>

      <main className="flex-1 mt-8 flex flex-col overflow-hidden" data-testid="tv-substitutions">
        {subs.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <p className="font-heading text-4xl md:text-6xl font-bold text-muted-foreground">
              Nessuna sostituzione in programma
            </p>
            <p className="text-xl md:text-2xl text-muted-foreground/70 mt-4">
              Non ci sono sostituzioni per oggi né per i prossimi giorni.
            </p>
          </div>
        ) : (
          <>
            {showNext && (
              <div data-testid="tv-nextday-banner" className="shrink-0 mb-5 rounded-md bg-primary/15 border border-primary/40 px-6 py-4 text-center">
                <span className="text-base md:text-lg uppercase tracking-widest text-muted-foreground">
                  Le sostituzioni di oggi sono terminate · Prossimo giorno
                </span>
                <div className="font-heading text-3xl md:text-4xl font-black capitalize mt-1 text-primary">
                  {niceDate(nextDay.date)}
                </div>
              </div>
            )}
            <div ref={scrollRef} className="flex-1 overflow-hidden">
            <div className="space-y-4">
            {subs.map((s, i) => (
              <div
                key={s.id}
                data-testid={`tv-row-${s.id}`}
                style={{ animationDelay: `${i * 80}ms` }}
                className={`animate-in-row grid grid-cols-12 items-center gap-6 rounded-md px-8 py-6 border ${
                  s.is_current
                    ? "tv-current-row border-transparent text-primary-foreground shadow-lg"
                    : "bg-card border-border"
                }`}
              >
                <div className="col-span-4 flex items-center gap-4">
                  <Clock className={`h-8 w-8 ${s.is_current ? "text-primary-foreground" : "text-primary"}`} />
                  <div>
                    <div className="text-4xl md:text-5xl font-bold tracking-tight tabular-nums">{s.slot_label}</div>
                    <div className={`text-lg ${s.is_current ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                      {s.slot}ª ora {s.is_current && "· IN CORSO"}
                    </div>
                  </div>
                </div>
                <div className="col-span-5">
                  <div className={`text-lg uppercase tracking-widest ${s.is_current ? "text-primary-foreground/70" : "text-muted-foreground"}`}>Docente</div>
                  <div className="text-4xl md:text-5xl font-bold tracking-tight truncate">{s.teacher_name}</div>
                </div>
                <div className="col-span-3 flex items-center justify-end gap-3">
                  <MapPin className={`h-8 w-8 ${s.is_current ? "text-primary-foreground" : "text-primary"}`} />
                  <div className="text-right">
                    <div className={`text-lg uppercase tracking-widest ${s.is_current ? "text-primary-foreground/70" : "text-muted-foreground"}`}>Classe</div>
                    <div className="text-5xl md:text-6xl font-black tracking-tight">{s.class_name}</div>
                  </div>
                </div>
              </div>
            ))}
            </div>
            </div>
          </>
        )}
      </main>

      <footer className="mt-6 pt-4 border-t border-border flex items-center justify-between text-muted-foreground text-sm md:text-base">
        <span>Aggiornamento automatico ogni 20 secondi</span>
        <span className="font-mono">Le ore concluse vengono nascoste automaticamente</span>
      </footer>
    </div>
  );
}
